import React, { useId } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Skeleton } from "./States";
import { cx, formatPercent } from "./format";

/** Tiny trend line for stat cards. Colour defaults to Monk Gold. */
export const Sparkline = ({ data = [], color = "var(--cm-chart-1)", height = 36, className }) => {
  const gid = useId().replace(/:/g, "");
  const values = data.map(Number).filter(Number.isFinite);
  if (values.length < 2) return <div style={{ height }} className={className} />;
  const W = 200, H = height, max = Math.max(...values), min = Math.min(...values);
  const x = (i) => (i * W) / (values.length - 1);
  const y = (v) => 3 + (H - 6) * (1 - (v - min) / (max - min || 1));
  const d = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={cx("w-full block", className)} style={{ height }} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${W} ${H} L0 ${H} Z`} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
};

/**
 * KPI tile: label, big value, change vs last period, optional sparkline.
 *   change: number (percent) → green/red arrow; `changeLabel` e.g. "vs yesterday"
 *   goodWhen: "up" | "down" (e.g. pending cash-outs are good when down)
 */
export const StatCard = ({
  label, value, icon: Icon, change, changeLabel, hint, trend, goodWhen = "up", loading = false,
  tone, onClick, className,
}) => {
  const hasChange = change !== null && change !== undefined && Number.isFinite(Number(change));
  const up = hasChange && Number(change) > 0;
  const flat = hasChange && Number(change) === 0;
  const good = hasChange && !flat && (goodWhen === "up" ? up : !up);
  const Tag = onClick ? "button" : "div";
  const accent = {
    gold: "text-cm-gold-text", success: "text-cm-success", danger: "text-cm-danger", warning: "text-cm-warning", info: "text-cm-info",
  }[tone];
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cx(
        "text-left bg-cm-surface border border-cm-border rounded-cm-lg p-4 pb-3 flex flex-col gap-1.5 min-w-0",
        onClick && "hover:border-cm-border-strong transition-colors cursor-pointer",
        className,
      )}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold text-cm-muted">
        {Icon && <Icon size={14} className={accent || ""} aria-hidden="true" />}
        <span className="truncate">{label}</span>
      </span>
      {loading ? (
        <Skeleton className="h-7 w-2/3 my-0.5" />
      ) : (
        <span className={cx("font-display text-[26px] leading-tight font-extrabold tabular-nums truncate", accent || "text-cm-text")}>
          {value ?? "—"}
        </span>
      )}
      {(hasChange || hint || changeLabel) && (
        <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-cm-muted min-h-[18px]">
          {hasChange && (
            <span className={cx("inline-flex items-center gap-0.5 font-semibold", flat ? "" : good ? "text-cm-success" : "text-cm-danger")}>
              {!flat && (up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />)}
              {formatPercent(change, { sign: true })}
            </span>
          )}
          {changeLabel && <span>{changeLabel}</span>}
          {hint && <span className="truncate">{hint}</span>}
        </span>
      )}
      {trend && trend.length > 1 && (
        <Sparkline
          data={trend}
          className="mt-1"
          color={goodWhen === "down" ? "var(--cm-warning)" : "var(--cm-chart-1)"}
        />
      )}
    </Tag>
  );
};

export default StatCard;
